import { DateTime } from 'luxon'
import { useCallback, useEffect, useRef, useState } from 'react'

import { BidType, MARKET_TIMEZONE, MarketProgram } from '@/features/bidding/constants'
import { isPtuEditable as isPtuEditableUtil } from '@/features/bidding/utils/isPtuEditable'

/**
 * Minutes before the start of a PTU when it becomes non-editable
 * According to requirement: "We will stop bidding for a PTU at QH-50"
 */
const PTU_EXPIRATION_MINUTES = 50

/**
 * Hook that provides PTU expiration functionality
 * Determines which PTUs are editable based on time constraints (QH-50)
 * Updates every minute to keep the editable state accurate
 */
export const usePtuExpiration = () => {
  // Reference to store timer IDs for cleanup
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Use state to track the current time and update it regularly
  const [currentTime, setCurrentTime] = useState<DateTime>(DateTime.now().setZone(MARKET_TIMEZONE))

  // Update current time every minute to keep PTU expiration state accurate
  useEffect(() => {
    // Initial update to sync with the current exact time
    setCurrentTime(DateTime.now().setZone(MARKET_TIMEZONE))

    // Set up interval to update every minute
    const intervalId = setInterval(
      () => {
        setCurrentTime(DateTime.now().setZone(MARKET_TIMEZONE))
      },
      60 * 1000, // Update every minute
    )

    // Store interval ID in ref for cleanup
    timerRef.current = intervalId

    // Clean up timer on component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const isPtuEditable = useCallback(
    (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram): boolean => {
      return isPtuEditableUtil(currentTime, ptuStart, bidType, marketProgram)
    },
    [currentTime],
  )

  const isFeatureEnabled = (bidType: BidType, marketProgram?: MarketProgram): boolean => {
    return (
      bidType === BidType.ENERGY &&
      (marketProgram === MarketProgram.MFRR_UP || marketProgram === MarketProgram.MFRR_DOWN)
    )
  }

  /**
   * Returns the current time threshold before which PTUs are not editable
   */
  const getExpirationThreshold = useCallback(
    (bidType: BidType, marketProgram?: MarketProgram): DateTime | null => {
      const isMfrrEnergy = isFeatureEnabled(bidType, marketProgram)

      if (!isMfrrEnergy) {
        return null
      }

      return currentTime.plus({ minutes: PTU_EXPIRATION_MINUTES })
    },
    [currentTime],
  )

  return {
    isFeatureEnabled,
    currentTime,
    isPtuEditable,
    getExpirationThreshold,
    expirationMinutes: PTU_EXPIRATION_MINUTES,
  }
}
